import { useEffect, useMemo, useState } from "react";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import {
  getBusinessProfile,
  getBusinessProfileSchema,
  putBusinessProfile,
  getDottedPath,
  expandDottedKeys,
} from "../../lib/api/businessProfile";

/**
 * Humanize the leaf segment of a dotted path.
 *   "rrc.w3a.cementing_company_name" -> "Cementing Company Name"
 */
function humanizeLeaf(path: string): string {
  const leaf = path.split(".").pop() ?? path;
  return leaf
    .split("_")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

export default function BusinessProfileForm() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [required, setRequired] = useState<string[]>([]);
  const [optional, setOptional] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [missingRequired, setMissingRequired] = useState<string[]>([]);

  // Load schema + current profile in parallel
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const [schema, profile] = await Promise.all([
          getBusinessProfileSchema("rrc", "w3a"),
          getBusinessProfile(),
        ]);
        if (cancelled) return;

        setRequired(schema.required);
        setOptional(schema.optional);

        const seeded: Record<string, string> = {};
        for (const path of [...schema.required, ...schema.optional]) {
          const existing = getDottedPath(profile.data, path);
          seeded[path] =
            existing === undefined || existing === null ? "" : String(existing);
        }
        setValues(seeded);

        // Initial missing check based on profile data (not local form state)
        const missing = schema.required.filter((p) => {
          const v = getDottedPath(profile.data, p);
          return v === undefined || v === null || v === "";
        });
        setMissingRequired(missing);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Failed to load business profile"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-dismiss success line after 3s
  useEffect(() => {
    if (savedAt == null) return;
    const t = setTimeout(() => setSavedAt(null), 3000);
    return () => clearTimeout(t);
  }, [savedAt]);

  const handleChange = (path: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setValues((prev) => ({ ...prev, [path]: next }));
    };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      // Skip empty-string entries so we don't clobber existing data
      const nonEmpty: Record<string, string> = {};
      for (const [path, val] of Object.entries(values)) {
        if (val !== "") nonEmpty[path] = val;
      }
      const payload = expandDottedKeys(nonEmpty);
      await putBusinessProfile(payload);

      // Re-fetch profile to verify what landed server-side
      const refreshed = await getBusinessProfile();

      // Refresh seeded values from server
      const seeded: Record<string, string> = {};
      for (const path of [...required, ...optional]) {
        const existing = getDottedPath(refreshed.data, path);
        seeded[path] =
          existing === undefined || existing === null ? "" : String(existing);
      }
      setValues(seeded);

      const missing = required.filter((p) => {
        const v = getDottedPath(refreshed.data, p);
        return v === undefined || v === null || v === "";
      });
      setMissingRequired(missing);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save business profile"
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldRows = useMemo(
    () => ({
      required,
      optional,
    }),
    [required, optional]
  );

  if (loading) {
    return (
      <div className="flex items-center py-8">
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
        <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">
          Loading business profile...
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
        <p className="text-sm font-medium text-error-700 dark:text-error-400">
          {loadError}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <div className="mb-5 lg:mb-7">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Business Profile
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Defaults used when generating RRC W-3A filings for this tenant.
          </p>
        </div>

        {missingRequired.length > 0 && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
            <p className="text-sm font-medium text-error-700 dark:text-error-400">
              Missing required fields:
            </p>
            <ul className="mt-1 list-disc pl-5 text-sm text-error-700 dark:text-error-400">
              {missingRequired.map((p) => (
                <li key={p}>{humanizeLeaf(p)}</li>
              ))}
            </ul>
          </div>
        )}

        {saveError && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
            <p className="text-sm text-error-700 dark:text-error-400">
              {saveError}
            </p>
          </div>
        )}

        {savedAt != null && (
          <div className="mb-4 rounded-lg bg-success-50 p-3 dark:bg-success-500/15">
            <p className="text-sm text-success-700 dark:text-success-400">
              Business profile saved.
            </p>
          </div>
        )}

        <div className="space-y-6">
          <section>
            <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Required (RRC W-3A)
            </h4>
            <div className="grid gap-5 md:grid-cols-2">
              {fieldRows.required.map((path) => {
                const id = `bp-${path.replace(/\./g, "-")}`;
                return (
                  <div key={path}>
                    <Label htmlFor={id}>
                      {humanizeLeaf(path)}{" "}
                      <span className="text-error-500">*</span>
                    </Label>
                    <Input
                      id={id}
                      name={path}
                      type="text"
                      value={values[path] ?? ""}
                      onChange={handleChange(path)}
                      required
                    />
                  </div>
                );
              })}
            </div>
          </section>

          {fieldRows.optional.length > 0 && (
            <section>
              <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Optional
              </h4>
              <div className="grid gap-5 md:grid-cols-2">
                {fieldRows.optional.map((path) => {
                  const id = `bp-${path.replace(/\./g, "-")}`;
                  return (
                    <div key={path}>
                      <Label htmlFor={id}>{humanizeLeaf(path)}</Label>
                      <Input
                        id={id}
                        name={path}
                        type="text"
                        value={values[path] ?? ""}
                        onChange={handleChange(path)}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
